/**
 * Responsible for creating an toggleContent based on elements existing in the DOM
 * @module modules/toggleContent
 */

import $ from 'jquery';

import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import mergeOptions from '../lib/merge-options';
import Keyboard from './keyboard';

export const PLUGIN_NAME = 'toggleDropdown';
export const CLICK_EVENT = 'click';
export const CLOSE_BUTTON = 'js-close-dropdown';

export const DEFAULTS = {
  scrollLock: true,
  parentContainer: '',
  dialogContainer: 'js-page-header__nav-list',
  trapFocusFunc: Keyboard.trapFocus.bind(Keyboard),
  releaseFocusFunc: Keyboard.releaseFocus.bind(Keyboard),
};

const closeAllToggleDropdowns = () => {
  $("[toggle-body-lock='true']").toggleDropdown('toggle');
};

/**
 * Handles the height of the dropdown so it takes over the screen
 * @private
 */
function titleHeaderDropdownHeight() {
  const headerDropdown = $('#plpheader_dropdown');
  if (headerDropdown.length) {
    const docHeight = document.documentElement.clientHeight;
    const $jsPageheaderNavList = $('.js-page-header__nav-list');
    const titleHeight = $('.js-page-header').height();

    $jsPageheaderNavList.css({ maxHeight: (docHeight - titleHeight) });
  }
}

/**
 * Creates a close button
 */
const createCloseButton = () => {
  const closeBlock = document.createElement('div');
  closeBlock.classList.add('u-padding-a--lg');
  const buttonTag = document.createElement('button');
  buttonTag.classList.add('action-button', 'action-button--full', 'page-header__link--close', CLOSE_BUTTON);
  buttonTag.innerHTML = 'Close Menu<span class="u-screen-reader">dropdown</span>';
  closeBlock.appendChild(buttonTag);
  return closeBlock;
}

/**
 * Closes Dropdown
  * @param {jQuery} $el - Containing the elements for the toggleContent
  * @param {jQuery} $toggleThis - Element to toggle
  * @param {jQuery} $parentContainer - Container that contains trigger btn and dropdown
  * @param {Object} opts - Options, defaults and data values
 */
const closeDropdown = ($el, $toggleThis, $parentContainer, opts) => {
  $toggleThis.slideUp();
  $toggleThis.removeClass('is-active');
  $el.removeClass('is-active');
  if (opts.scrollLock) {
    $el.attr('toggle-body-lock', '');
    enableBodyScroll($toggleThis[0]);
    $toggleThis.off('scroll.toggledropdown');
  }
  $el.attr('aria-expanded', "false");
  $parentContainer.attr('aria-modal', "false");
  opts.releaseFocusFunc($el.closest('.js-page-header'), $el);
}

/**
 * Opens Dropdown
  * @param {jQuery} $el - Containing the elements for the toggleContent
  * @param {jQuery} $toggleThis - Element to toggle
  * @param {jQuery} $parentContainer - Container that contains trigger btn and dropdown
  * @param {Object} opts - Options, defaults and data values
 */
const openDropdown = ($el, $toggleThis, $parentContainer, opts) => {
  $toggleThis.slideDown();
  $toggleThis.addClass('is-active');
  $el.addClass('is-active');
  if (opts.scrollLock) {
    $el.attr('toggle-body-lock', 'true');
    $('html, body').scrollTop($('.js-page-header').offset().top);
    disableBodyScroll($toggleThis[0]);
  }
  $el.attr('aria-expanded', "true");
  $parentContainer.attr('aria-modal', "true");
  opts.trapFocusFunc($el.closest('.js-page-header'));
}


/**
 * Handles the click event when a label has been iteracted with.
 * @private
 * @param {jQuery.Event} evt - A jQuery event
 * @param {jQuery} $el - Containing the elements for the toggleContent
 * @param {Object} options - Options used for wiring up the toggleContent
 */
const itemClickHandler = ($el, $toggleThis, $parentContainer, opts) => {
  const dialogContainer = `.${opts.dialogContainer}`;
  const $hasCloseButton = $(opts.parentContainer).find(`.${CLOSE_BUTTON}`).length;

  if ($el.hasClass('is-active')) {
    closeDropdown($el, $toggleThis, $parentContainer, opts);
  } else {
    openDropdown($el, $toggleThis, $parentContainer, opts);

    // If close button is not present then create it and bind closeOtherToggles event to it
    if ($hasCloseButton <= 0) {
      const $plpDialog = $parentContainer.find(dialogContainer);
      const dropdownCloseBtn = createCloseButton();
      $plpDialog.append(dropdownCloseBtn);
      $parentContainer.find(`.${CLOSE_BUTTON}`).on('click', () => closeDropdown($el, $toggleThis, $parentContainer, opts));
    } else {
      $parentContainer.find(`.${CLOSE_BUTTON}`).on('click', () => closeDropdown($el, $toggleThis, $parentContainer, opts));
    }
  }
  titleHeaderDropdownHeight();
};

/**
 * Applies any sort of event handlers for the toggleContent.
 * @private
 * @param {jQuery} $el - Containing the elements for the toggleContent
 * @param {Object} options - Options used for wiring up the toggleContent
 * @see DEFAULTS
 */
const applyEventHandlers = ($el, opts = {}) => {
  const $toggleThis = $($el.attr('data-toggle-select-this'));
  const $parentContainer = $el.closest(opts.parentContainer);
  $el
    .on(CLICK_EVENT, () => itemClickHandler($el, $toggleThis, $parentContainer, opts));
    $(document).on('keyup', function (e) {
      if (e.key === "Escape" && $el.hasClass('is-active')) { // escape key maps to keycode `27`
        closeDropdown($el, $toggleThis, $parentContainer, opts);
        $el.trigger('focus');
      }
    });
};

/**
 * Initializes an toggleContent based on provided parameters.
 * @param {HTMLElement} el - An element containing the elements for the
 * toggleContent
 * @param {Object} options - Options used for wiring up the toggleContent
 * @see DEFAULTS
 */
export default function toggleDropdown(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);

  applyEventHandlers($el, opts);
  return {
    toggle: () => itemClickHandler($el, opts),
  };
}

$.toggleDropdown = (options) => {
  $('<div/>').toggleDropdown(options);
};

$.toggleDropdown.close = () => {
  closeAllToggleDropdowns();
};

// Expose the function as a jQuery plugin for ease of use
registerJQueryPlugin(PLUGIN_NAME, toggleDropdown);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/toggle-dropdown.js